import { useEffect, useRef, useState } from 'react'

import { Check, ChevronDown, Loader2, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
    type HotelReservationEmailType,
    sendHotelReservationEmail,
} from '@/shared/api/reservationsAPI'
import { cn } from '@/shared/lib/styles/class-merge'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { useToast } from '@/shared/ui/use-toast'

type SendHotelEmailDropdownProps = {
    reservationId: string
}

const EMAIL_OPTIONS: HotelReservationEmailType[] = ['confirmation', 'created', 'budget', 'rejected']
const SUCCESS_FEEDBACK_MS = 1000

export function SendHotelEmailDropdown({ reservationId }: SendHotelEmailDropdownProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [isOpen, setIsOpen] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)
    const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current)
        }
    }, [])

    const emailLabels: Record<HotelReservationEmailType, string> = {
        confirmation: t('reservation.viewer.sendEmail.confirmation', 'Confirmación de reserva'),
        created: t('reservation.viewer.sendEmail.created', 'Solicitud recibida'),
        budget: t('reservation.viewer.sendEmail.budget', 'Presupuesto'),
        rejected: t('reservation.viewer.sendEmail.rejected', 'Reserva rechazada'),
    }

    const handleSend = async (emailType: HotelReservationEmailType) => {
        setIsSending(true)
        setIsOpen(false)

        try {
            await sendHotelReservationEmail({ reservationId, emailType })
            toast({
                title: t('reservation.viewer.sendEmail.success', 'Correo enviado'),
                description: t(
                    'reservation.viewer.sendEmail.successDescription',
                    'Se ha enviado el correo "{{emailType}}" al cliente.',
                    { emailType: emailLabels[emailType] },
                ),
            })
            setShowSuccess(true)
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current)
            successTimeoutRef.current = setTimeout(() => setShowSuccess(false), SUCCESS_FEEDBACK_MS)
        } catch (error) {
            console.error('Error sending hotel reservation email:', error)
            toast({
                title: t('reservation.viewer.sendEmail.error', 'Error al enviar el correo'),
                description: t(
                    'reservation.viewer.sendEmail.errorDescription',
                    'No se pudo enviar el correo. Inténtalo de nuevo.',
                ),
                variant: 'destructive',
            })
        } finally {
            setIsSending(false)
        }
    }

    const isBusy = isSending || showSuccess

    return (
        <Popover open={isOpen} onOpenChange={open => !isBusy && setIsOpen(open)}>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    size='sm'
                    className={cn(
                        'gap-2 transition-colors',
                        showSuccess &&
                            'border-green-600 bg-green-50 text-green-700 hover:bg-green-50 hover:text-green-700',
                    )}
                    disabled={isBusy}
                >
                    {isSending ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                    ) : showSuccess ? (
                        <Check className='h-4 w-4' />
                    ) : (
                        <Mail className='h-4 w-4' />
                    )}
                    {showSuccess
                        ? t('reservation.viewer.sendEmail.sent', 'Enviado')
                        : t('reservation.viewer.sendEmail.button', 'Enviar correo')}
                    {!showSuccess && !isSending && <ChevronDown className='h-4 w-4 opacity-50' />}
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-56 p-1' align='end'>
                <div className='flex flex-col'>
                    {EMAIL_OPTIONS.map(emailType => (
                        <Button
                            key={emailType}
                            variant='ghost'
                            size='sm'
                            className='justify-start font-normal'
                            onClick={() => handleSend(emailType)}
                            disabled={isBusy}
                        >
                            {emailLabels[emailType]}
                        </Button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    )
}
